/**
 * Self-enrollment on the participant's own phone (BOOTH-SELF-ENROL).
 *
 * Kiosk (staff, signed in): the booth laptop gets a single-use code, shows it
 * as a QR code and polls it; once claimed it fetches the next one.
 * Join (public): the participant's phone claims the code — keeping the session
 * token it gets the first time — then reads the badge and submits.
 */
import { apiClient } from './client';
import type {
  BadgeRead,
  ConsentDefinition,
  IntakeForm,
  IntakeSubmission,
  SurveyDefinition,
} from './intake';

export interface KioskCode {
  code_id: string;
  url: string;
  expires_at: string;
}

export type KioskCodeState = 'open' | 'claimed' | 'completed' | 'expired';

export interface KioskStatus {
  state: KioskCodeState;
  in_progress: number;
  done_today: number;
  recent: { name: string | null; at: string; possible_duplicate: boolean }[];
}

export async function newKioskCode(tenantId: string, kioskId: string): Promise<KioskCode> {
  const { data } = await apiClient.post<KioskCode>(
    `/api/v1/tenants/${tenantId}/intake/kiosk/codes`,
    { kiosk_id: kioskId },
  );
  return data;
}

export async function kioskStatus(tenantId: string, codeId: string): Promise<KioskStatus> {
  const { data } = await apiClient.get<KioskStatus>(
    `/api/v1/tenants/${tenantId}/intake/kiosk/codes/${codeId}`,
  );
  return data;
}

export interface JoinForms {
  /** Only on the first claim from this phone — keep it for the session. */
  session: string | null;
  organisation: string;
  consent_form: IntakeForm<ConsentDefinition> | null;
  survey_form: IntakeForm<SurveyDefinition> | null;
  badge_reading: boolean;
}

const headers = (session: string | null) => (session ? { 'X-Join-Session': session } : {});

export async function claimJoin(code: string, session: string | null): Promise<JoinForms> {
  const { data } = await apiClient.post<JoinForms>(
    `/api/v1/join/${encodeURIComponent(code)}/claim`,
    {},
    { headers: headers(session) },
  );
  return data;
}

export async function joinReadBadge(code: string, session: string, image: string): Promise<BadgeRead> {
  const { data } = await apiClient.post<BadgeRead>(
    `/api/v1/join/${encodeURIComponent(code)}/badge-read`,
    { image },
    { headers: headers(session) },
  );
  return data;
}

export async function joinSubmit(
  code: string,
  session: string,
  submission: IntakeSubmission,
): Promise<void> {
  await apiClient.post(`/api/v1/join/${encodeURIComponent(code)}/submit`, submission, {
    headers: headers(session),
  });
}
